import Footer from '@/components/Layout/Footer';
import { ComponentProps as ContentComponentProps } from '@/components/Internals/ContentComposer';
import { SchemaType } from '@/components/Internals/InternalSchema';
import Layout from '@/components/Layout';
import Accordion from '@/components/Layout/Accordion';
import Titulo from '@/components/Layout/Title';
import WrapperContent from '@/components/Layout/WrapperContent';
import { NavBarContent } from '@/components/Navbar';
import ProfileLinks, { ProfileLinksI } from '@/components/Profile/ProfileHeader/ProfileLinks';
import { api, mimir, site } from '@/services/api';
import { HeadTags, NavBarContentData, Segmentations, TimeLineApi } from '@/shared/types/home.types';
import { mglGray } from '@/styles/designSystemColors';
import getConcurrentPromises from '@/utils/getConcurrentPromises';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import ImageComponent from '@/components/Layout/Image';
import Container from '@/components/Layout/Container';
import FeaturedLastNews from '@/components/Home/FeaturedLastNews';
import FeaturedProductExclusive from '@/components/Layout/FeaturedProductExclusive';
import CompanyHero from '@/components/Internals/CompanyHero';
import PageSummaryNavigation from '@/components/Layout/PageSummaryNavigation';

const FeaturedProducts = dynamic(() => import('@/components/Home/FeaturedProducts'), { ssr: false });
const VideoSlidersYoutube = dynamic(() => import('@/components/Home/FeaturedVideosPlayer'), { ssr: false });

const SUMMARY_ANCHORS = [
  { key: 'oppo', label: 'OPPO', href: '#oppo' },
  { key: 'analise', label: 'Notícias', href: '#noticias' },
  { key: 'produtos', label: 'Produtos', href: '#produtos' },
  { key: 'ofertas-oppo', label: 'Ofertas OPPO', href: '#ofertas-oppo' },
  { key: 'videos', label: 'Vídeos', href: '#videos' },
  { key: 'faq', label: 'FAQ', href: '#perguntas-frequentes' },
] as const;

type AnchorItem = (typeof SUMMARY_ANCHORS)[number];

const FALLBACK_SCROLL_MARGIN = 140;
const HEADER_HEIGHT = 0;
const SUMMARY_TOP_GAP = 0;

export interface CompanyProfileProps {
  responseData: {
    status: number;
    data: {
      nome: string;
      codigoBolsa?: string;
      imagem: {
        url: string;
        blurhash: string;
      };
      subsidiariaDe?: string;
      fundacao?: {
        data?: string;
        local?: string;
      };
      links: ProfileLinksI['links'];
      texto?: ContentComponentProps[] | string;
      produtosEmpresa?: {
        titulo: string;
        imagem: {
          url: string;
          blurhash: string;
        };
        url: string;
        rumor: boolean;
      }[];
    };
    ssr: HeadTags[];
    segmentacoes: Segmentations[];
    schema: SchemaType;
    headers: {
      'Cache-control': string;
      Expires: string;
    };
  };
  navBarData: NavBarContent;
  timelineData?: TimeLineApi;
}

export default function CompanyProfile({ responseData, navBarData, timelineData = null }: CompanyProfileProps) {
  const router = useRouter();
  const { empresa = 'oppo' } = router.query;
  const empresaSlug = typeof empresa === 'string' ? empresa : Array.isArray(empresa) ? empresa[0] : 'oppo';

  const { data, segmentacoes, schema, ssr } = responseData;
  const { nome, imagem, links, texto, produtosEmpresa } = data;

  const confirmedProducts = useMemo(() => {
    if (!produtosEmpresa) return [];
    return produtosEmpresa.filter(product => !product.rumor);
  }, [produtosEmpresa]);

  const hasProducts = Boolean(confirmedProducts && confirmedProducts.length > 0);

  const anchorItems = useMemo<AnchorItem[]>(() => {
    return SUMMARY_ANCHORS.filter(item => {
      if (item.key === 'produtos') return hasProducts;
      return true;
    });
  }, [hasProducts]);

  const heroFallbackParagraphs = useMemo(
    () => [
      `${nome} é uma marca global que desenvolve produtos e serviços de tecnologia inteligente voltados para o dia a dia, combinando design minimalista com experiências sofisticadas.`,
      '"Benfen" é um valor essencial e crença inabalável para a empresa: manter o usuário no centro e usar a paixão pela inovação para criar soluções que transformem o cotidiano.',
    ],
    [nome]
  );

  const heroOppoBackgroundUrl = 'https://t.ctcdn.com.br/Y0EDOZCj5eo_j3Z-9LAEug9igwk=/i1070389.webp';

  const summaryStickyTop = HEADER_HEIGHT + SUMMARY_TOP_GAP;
  const [sectionsScrollMargin, setSectionsScrollMargin] = useState(FALLBACK_SCROLL_MARGIN);

  const FAQItems = [
    {
      title: 'O que é a OPPO e qual seu propósito como marca?',
      content:
        'A OPPO é uma empresa global de tecnologia que desenvolve produtos e serviços inteligentes voltados para o uso cotidiano. Seus dispositivos combinam design minimalista, desempenho avançado e uma experiência intuitiva, com o objetivo de tornar a tecnologia mais humana e acessível. O propósito da marca é inspirar as pessoas a explorarem sua criatividade de forma simples e significativa, ajudando-as a viver e expressar a beleza da vida em cada momento.',
    },
    {
      title: 'Quais são os valores centrais da OPPO?',
      content:
        'A OPPO se baseia em princípios sólidos que norteiam sua atuação global. O principal deles é o conceito chinês de “Benfen”, que significa fazer o que é certo, da maneira certa, mantendo sempre uma mente aberta na busca pela verdade. Além disso, a empresa coloca o usuário no centro de todas as decisões, valorizando a inovação tecnológica, o design com propósito e o compromisso com a qualidade. Há também uma forte paixão pela criação e pela melhoria constante da experiência do consumidor, sempre com foco em impacto positivo e duradouro.',
    },
    {
      title: 'A OPPO oferece assistência técnica no Brasil? Como funciona o suporte ao cliente?',
      content:
        'Sim, a OPPO oferece assistência técnica autorizada no Brasil, com centros de serviço em diversas regiões e suporte digital acessível. A empresa entende que um bom atendimento pós-venda é essencial para a confiança do consumidor, por isso investe em canais de atendimento humanizados e eficientes. É possível tirar dúvidas, solicitar reparos e acompanhar o status do serviço por meio do site oficial ou atendimento via WhatsApp. A garantia dos produtos segue as regras do Código de Defesa do Consumidor brasileiro, e a OPPO está comprometida em oferecer suporte rápido e transparente para garantir tranquilidade a quem escolhe a marca.',
    },
    {
      title: 'Em quantos países a OPPO opera e qual é sua presença global?',
      content:
        'Fundada em 2004, a OPPO expandiu-se rapidamente e hoje está presente em mais de 70 países e regiões. Conta com uma equipe global de mais de 40 mil funcionários, o que reflete o alcance e a diversidade cultural de suas operações. Essa presença internacional permite que a marca adapte seus produtos às necessidades de diferentes públicos, mantendo a mesma qualidade e filosofia em todos os mercados.',
    },
    {
      title: 'Como é a qualidade de fabricação dos produtos OPPO?',
      content:
        'A OPPO investe pesadamente em qualidade e controle de produção. Seus dispositivos são fabricados de acordo com os padrões mais rigorosos da indústria e passam por processos de teste minuciosos em cada etapa. A empresa possui nove centros de produção espalhados pelo mundo, todos equipados com tecnologia de ponta. Além disso, seus produtos cumprem as exigências das principais operadoras de telefonia globais, o que assegura compatibilidade e desempenho confiável em qualquer mercado.',
    },
    {
      title: 'Qual é o slogan da OPPO e o que ele significa?',
      content:
        'O slogan da OPPO é “Make Your Moment”, uma expressão que reflete a filosofia central da marca: transformar momentos comuns em experiências extraordinárias. A ideia é que cada pessoa possa aproveitar o presente, criar algo novo e se expressar plenamente, usando a tecnologia como uma ferramenta para ampliar a própria criatividade e autenticidade.',
    },
    {
      title: 'Quantos usuários usam o sistema operacional da OPPO (ColorOS)?',
      content:
        'O sistema operacional da OPPO, chamado ColorOS, é utilizado por mais de 700 milhões de pessoas em todo o mundo. Essa base de usuários demonstra a robustez, a estabilidade e a aceitação global do ecossistema da marca, que combina desempenho com uma interface amigável e altamente personalizável.',
    },
    {
      title: 'Com quais marcas ou organizações a OPPO colabora?',
      content:
        'A OPPO mantém parcerias estratégicas com grandes nomes globais tanto no campo tecnológico como esportivo e de mídia. A marca estabeleceu colaboração com a UEFA Champions League e trabalha com a Hasselblad em sistemas de fotografia móvel de alta performance. Em outubro de 2025, firmou também uma parceria com o Canaltech, integrando conteúdos, promoções ou iniciativas conjuntas que buscam aproximar o público consumidor brasileiro à inovação da OPPO através da cobertura especializada do Canaltech.',
    },
    {
      title: 'Por que confiar em uma marca como a OPPO?',
      content:
        'A China é hoje um dos maiores polos de inovação tecnológica do mundo, e a OPPO está entre as marcas líderes dessa transformação. Com mais de 20 anos de experiência, a empresa investe pesado em pesquisa e desenvolvimento, possui centros próprios de inovação e já foi pioneira em diversas tecnologias móveis. Sua presença em mais de 70 países e parcerias com empresas reconhecidas mundialmente demonstram a solidez da marca. No Brasil, a OPPO respeita normas locais, oferece suporte ao consumidor e tem como compromisso oferecer produtos confiáveis e duráveis, valorizando a experiência do usuário brasileiro com a mesma seriedade com que atua em mercados como Europa e Ásia.',
    },
    {
      title: 'Há quanto tempo a OPPO atua no Brasil?',
      content:
        'A OPPO iniciou oficialmente sua atuação no Brasil em 2022, trazendo para o país seus smartphones e tecnologias que já conquistaram usuários em diversos mercados. Desde então, tem expandido sua presença com parceiros locais, canais de venda confiáveis, suporte técnico autorizado e uma comunicação mais próxima do consumidor brasileiro. Apesar de ser relativamente recente no Brasil, a OPPO já demonstrou seu compromisso com o mercado nacional ao investir em estrutura, atendimento e conteúdo local, buscando construir uma relação de longo prazo com os brasileiros.',
    },
  ];
  return (
    <>
      <Layout layoutData={{ navBarData, segmentacoes, tagsData: ssr, schema }}>
        <div className="flex w-full flex-col items-center bg-white">
          <Container className="flex flex-col items-center justify-center pb-6">
            <ImageComponent src={imagem.url} width={120} height={120} alt={nome} />
            {links && Object.values(links).length > 0 && <ProfileLinks links={links} type="empresa" />}
          </Container>

          <CompanyHero
            content={texto}
            companyName={nome}
            companySlug={empresaSlug}
            scrollMarginTop={sectionsScrollMargin}
            sectionId="oppo"
            backgroundImageUrl={heroOppoBackgroundUrl}
            heading={`Sobre a ${nome}`}
            fallbackParagraphs={heroFallbackParagraphs}
          />

          <PageSummaryNavigation
            anchors={anchorItems.map(({ href, label }) => ({ href, label }))}
            summaryStickyTop={summaryStickyTop}
            fallbackScrollMargin={FALLBACK_SCROLL_MARGIN}
            backgroundColor="white"
            onSectionsMarginChange={setSectionsScrollMargin}
          />

          <section id="noticias" className="w-full" style={{ scrollMarginTop: sectionsScrollMargin }}>
            {timelineData?.data?.timeline && (
              <FeaturedLastNews
                featuredLastNewsData={timelineData.data.timeline}
                rootPage="oppo"
                textTitle={`Últimas sobre ${nome}`}
                route={empresaSlug}
                routeType="empresa"
                useLoadMoreButton
                backgroundColor="bg-white"
                hideAds
              />
            )}
          </section>

          {hasProducts && (
            <section id="produtos" className="w-full" style={{ scrollMarginTop: sectionsScrollMargin }}>
              <FeaturedProducts featuredProductsData={confirmedProducts} title={`Produtos ${nome}`} shouldShowChip url={`/empresa/${empresaSlug}/produtos/`} />
            </section>
          )}

          <section id="ofertas-oppo" className="w-full" style={{ scrollMarginTop: sectionsScrollMargin }}>
            <FeaturedProductExclusive />
          </section>

          <section id="videos" className="w-full" style={{ scrollMarginTop: sectionsScrollMargin }}>
            <WrapperContent backgroundColor={mglGray.mglGray100}>
              <VideoSlidersYoutube />
            </WrapperContent>
          </section>

          <section id="perguntas-frequentes" className="w-full" style={{ scrollMarginTop: sectionsScrollMargin }}>
            <WrapperContent backgroundColor={mglGray.mglGray100} paddingOnBottom>
              <div className="m-4 md:mx-0">
                <Titulo texto={'Perguntas frequentes'} color="black" />
              </div>

              <Accordion items={FAQItems} blueTheme={true} />
            </WrapperContent>
          </section>
        </div>
      </Layout>
      <Footer />
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { host } = context.req.headers;
  const { empresa = 'oppo' } = context.query;

  const profileReq = { promise: site.get<CompanyProfileProps>(`/empresa/${empresa as string}/`), link: `/empresa/${empresa as string}/` };
  const responseMenuReq = { promise: api.get<NavBarContentData>(`/componentes/menu/`), link: `/componentes/menu/` };
  const timelinesReq = {
    promise: mimir.get<TimeLineApi>(`/timelines/ultimas/empresa/${empresa as string}`),
    link: `/timelines/ultimas/empresa/${empresa as string}`,
  };

  const { promiseData, data, statusCode } = await getConcurrentPromises({
    mainPromise: profileReq,
    navBarPromise: responseMenuReq,
    timelinePromise: timelinesReq,
    host,
  });

  if (data.redirect || data.notFound) return data;

  const navBarData: NavBarContentData = promiseData[responseMenuReq.link].data.data;
  const timelineData: TimeLineApi = promiseData[timelinesReq.link].data;

  const { headers } = data;
  const responseData = data;

  context.res.setHeader('Cache-Control', headers['Cache-control']);
  context.res.setHeader('Expires', headers.Expires);
  context.res.statusCode = statusCode;

  return {
    props: {
      responseData,
      navBarData,
      timelineData,
      headers,
    },
  };
};
